import i18n from 'i18n-js';
import * as types from './types';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import { uiActions } from 'state/modules/UI';
import * as invoiceActions from 'services/api/invoice';
import * as organizationsApi from 'services/api/organizations';
import fileDownload from 'js-file-download';

// Get invoices
const fetchInvoicesRequest = () => ({
  type: types.FETCH_INVOICES_REQUEST,
});

const fetchInvoicesSuccess = (payload) => ({
  type: types.FETCH_INVOICES_SUCCESS,
  payload,
});

const fetchInvoicesFailed = (error) => ({
  type: types.FETCH_INVOICES_FAILED,
  error,
});

export const fetchInvoicesReset = () => ({
  type: types.FETCH_INVOICE_BY_ID_RESET,
});

export const fetchInvoices = (params = {}, options) => {
  return async (dispatch) => {
    dispatch(fetchInvoicesRequest());

    const response = await invoiceActions.fetchInvoices(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchInvoicesSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    if (!isCancel(response)) {
      const error = getErrorMessage(response.data);
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );

      dispatch(fetchInvoicesFailed());
    }
  };
};

// Get invoice by ID
const fetchInvoiceRequest = () => ({
  type: types.FETCH_INVOICE_BY_ID_REQUEST,
});

const fetchInvoiceSuccess = (payload) => ({
  type: types.FETCH_INVOICE_BY_ID_SUCCESS,
  payload,
});

const fetchInvoiceFailed = (error) => ({
  type: types.FETCH_INVOICE_BY_ID_FAILED,
  error,
});

export const fetchInvoiceById = (id) => {
  return async (dispatch) => {
    dispatch(fetchInvoiceRequest());

    const response = await invoiceActions.fetchInvoiceById(id);

    if (response.ok) {
      return dispatch(fetchInvoiceSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchInvoiceFailed(error));
  };
};

// Get account emitter for an invoice
const fetchAccountEmitterRequest = () => ({
  type: types.FETCH_ACCOUNT_EMITTER_REQUEST,
});

const fetchAccountEmitterSuccess = (payload) => ({
  type: types.FETCH_ACCOUNT_EMITTER_SUCCESS,
  payload,
});

const fetchAccountEmitterFailed = (error) => ({
  type: types.FETCH_ACCOUNT_EMITTER_FAILED,
  error,
});

export const fetchAccountEmitterById = (id) => {
  return async (dispatch) => {
    dispatch(fetchAccountEmitterRequest());

    const response = await organizationsApi.fetchOrganizationById(id);

    if (response.ok) {
      return dispatch(fetchAccountEmitterSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccountEmitterFailed(error));
  };
};

// Process invoice by ID
const fetchProcessInvoiceRequest = () => ({
  type: types.FETCH_PROCESS_INVOICE_BY_ID_REQUEST,
});

const fetchProcessInvoiceSuccess = (payload) => ({
  type: types.FETCH_PROCESS_INVOICE_BY_ID_SUCCESS,
  payload,
});

const fetchProcessInvoiceFailed = (error) => ({
  type: types.FETCH_PROCESS_INVOICE_BY_ID_FAILED,
  error,
});

export const fetchProcessInvoiceById = (id, params) => {
  return async (dispatch) => {
    dispatch(fetchProcessInvoiceRequest());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'info',
        message: i18n.t('invoice.processing_balance_msg'),
      })
    );

    const response = await invoiceActions.fetchProcessInvoiceById(id, params);

    if (response.ok) {
      return dispatch(fetchProcessInvoiceSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchProcessInvoiceFailed(error));
  };
};

// Process invoices by IDs
const fetchProcessInvoicesRequest = () => ({
  type: types.FETCH_PROCESS_INVOICES_BY_IDS_REQUEST,
});

const fetchProcessInvoicesSuccess = (payload) => ({
  type: types.FETCH_PROCESS_INVOICES_BY_IDS_SUCCESS,
  payload,
});

const fetchProcessInvoicesFailed = (error) => ({
  type: types.FETCH_PROCESS_INVOICES_BY_IDS_FAILED,
  error,
});

export const fetchProcessInvoicesByIds = (ids) => {
  return async (dispatch) => {
    dispatch(fetchProcessInvoicesRequest(ids));
    const response = await invoiceActions.fetchProcessInvoicesByIds(ids);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('common.background_process'),
        })
      );
      return dispatch(fetchProcessInvoicesSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    return dispatch(fetchProcessInvoicesFailed(error));
  };
};

// Mark invoices as paid
const fetchMarkInvoicesAsPaidRequest = () => ({
  type: types.FETCH_INVOICES_AS_PAID_REQUEST,
});

const fetchMarkInvoicesAsPaidSuccess = (payload) => ({
  type: types.FETCH_INVOICES_AS_PAID_SUCCESS,
  payload,
});

const fetchMarkInvoicesAsPaidFailed = (error) => ({
  type: types.FETCH_INVOICES_AS_PAID_FAILED,
  error,
});

export const fetchMarkInvoicesAsPaidByIds = (ids) => {
  return async (dispatch) => {
    dispatch(fetchMarkInvoicesAsPaidRequest(ids));
    const response = await invoiceActions.fetchMarkInvoicesAsPaidByIds(ids);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('common.background_process'),
        })
      );
      return dispatch(fetchMarkInvoicesAsPaidSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    return dispatch(fetchMarkInvoicesAsPaidFailed(error));
  };
};

const fetchAddItemToInvoiceRequest = () => ({
  type: types.FETCH_ADD_ITEM_TO_INVOICE_REQUEST,
});

const fetchAddItemToInvoiceSuccess = (payload) => ({
  type: types.FETCH_ADD_ITEM_TO_INVOICE_SUCCESS,
  payload,
});

const fetchAddItemToInvoiceFailed = (error) => ({
  type: types.FETCH_ADD_ITEM_TO_INVOICE_FAILED,
  error,
});

export const fetchAddItemToInvoice = (payload, onSuccess) => {
  return async (dispatch) => {
    dispatch(fetchAddItemToInvoiceRequest());

    const response = await invoiceActions.fetchAddItemToInvoice(payload);

    if (response.ok) {
      if (onSuccess) {
        onSuccess();
      }

      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.added_invoice_item_success_msg'),
        })
      );
      return dispatch(fetchAddItemToInvoiceSuccess(response.data));
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAddItemToInvoiceFailed(error));
  };
};

const fetchUpdateInvoiceItemRequest = () => ({
  type: types.FETCH_UPDATE_INVOICE_ITEM_REQUEST,
});

const fetchUpdateInvoiceItemSuccess = (payload) => ({
  type: types.FETCH_UPDATE_INVOICE_ITEM_SUCCESS,
  payload,
});

const fetchUpdateInvoiceItemFailed = (error) => ({
  type: types.FETCH_UPDATE_INVOICE_ITEM_FAILED,
  error,
});

export const fetchUpdateInvoiceItem = (id, payload, onSuccess) => {
  return async (dispatch) => {
    dispatch(fetchUpdateInvoiceItemRequest());

    const response = await invoiceActions.fetchUpdateInvoiceItem(id, payload);

    if (response.ok) {
      if (onSuccess) {
        onSuccess();
      }
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.updated_invoice_item_success_msg'),
        })
      );
      return dispatch(fetchUpdateInvoiceItemSuccess(response.data));
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchUpdateInvoiceItemFailed(error));
  };
};

const fetchDeleteItemFromInvoiceRequest = () => ({
  type: types.FETCH_DELETE_ITEM_FROM_INVOICE_REQUEST,
});

const fetchDeleteItemFromInvoiceSuccess = (payload) => ({
  type: types.FETCH_DELETE_ITEM_FROM_INVOICE_SUCCESS,
  payload,
});

const fetchDeleteItemFromInvoiceFailed = () => ({
  type: types.FETCH_DELETE_ITEM_FROM_INVOICE_FAILED,
});

export const fetchDeleteItemFromInvoice = (payload) => {
  return async (dispatch) => {
    dispatch(fetchDeleteItemFromInvoiceRequest());
    const response = await invoiceActions.fetchDeleteItemFromInvoice(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.delete_invoice_item_success_msg'),
        })
      );
      return dispatch(fetchDeleteItemFromInvoiceSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchDeleteItemFromInvoiceFailed(error));
  };
};

const fetchInvoiceSourcesRequest = () => ({
  type: types.FETCH_INVOICE_SOURCES_REQUEST,
});

const fetchInvoiceSourcesSuccess = (payload) => ({
  type: types.FETCH_INVOICE_SOURCES_SUCCESS,
  payload,
});

const fetchInvoiceSourcesFailed = (error) => ({
  type: types.FETCH_INVOICE_SOURCES_FAILED,
  error,
});

export const fetchInvoiceSources = (payload) => {
  return async (dispatch) => {
    dispatch(fetchInvoiceSourcesRequest());
    const response = await invoiceActions.fetchInvoiceSources(payload);

    if (response.ok) {
      return dispatch(fetchInvoiceSourcesSuccess(response.data));
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchInvoiceSourcesFailed(error));
  };
};

const fetchRefundRequest = () => ({
  type: types.FETCH_REFUND_REQUEST,
});

const fetchRefundSuccess = (payload) => ({
  type: types.FETCH_REFUND_SUCCESS,
  payload,
});

const fetchRefundFailed = (error) => ({
  type: types.FETCH_REFUND_FAILED,
  error,
});

export const fetchRefund = (payload) => {
  return async (dispatch) => {
    dispatch(fetchRefundRequest());
    const response = await invoiceActions.fetchRefund(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.successfully_refunded'),
        })
      );
      return dispatch(fetchRefundSuccess(response.data));
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchRefundFailed(error));
  };
};

const resetRefundSuccess = () => ({
  type: types.RESET_REFUND_SUCCESS,
});

export const resetRefund = () => {
  return async (dispatch) => {
    dispatch(resetRefundSuccess());
  };
};

const fetchExportInvoiceRequest = () => ({
  type: types.FETCH_EXPORT_INVOICE_REQUEST,
});

const fetchExportInvoiceSuccess = () => ({
  type: types.FETCH_EXPORT_INVOICE_SUCCESS,
});

const fetchExportInvoiceFailed = (error) => ({
  type: types.FETCH_EXPORT_INVOICE_FAILED,
  error,
});

export const fetchExportInvoice = (id, type = 'invoice') => {
  return async (dispatch) => {
    dispatch(fetchExportInvoiceRequest());
    const response = await invoiceActions.fetchExportInvoice(id, type);

    if (response.ok) {
      fileDownload(response.data, `invoice-${id}.pdf`);
      return dispatch(fetchExportInvoiceSuccess());
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchExportInvoiceFailed(error));
  };
};

const fetchSendInvoiceByEmailRequest = () => ({
  type: types.FETCH_SEND_EMAIL_INVOICE_REQUEST,
});

const fetchSendInvoiceByEmailSuccess = () => ({
  type: types.FETCH_SEND_EMAIL_INVOICE_SUCCESS,
});

const fetchSendInvoiceByEmailFailed = (error) => ({
  type: types.FETCH_SEND_EMAIL_INVOICE_FAILED,
  error,
});

export const fetchSendInvoiceByEmail = (id, params = {}) => {
  return async (dispatch) => {
    dispatch(fetchSendInvoiceByEmailRequest());
    const response = await invoiceActions.fetchSendInvoiceByEmail(id, params);

    if (response.ok) {
      dispatch(fetchSendInvoiceByEmailSuccess());
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.send_email_success'),
        })
      );
    }

    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchSendInvoiceByEmailFailed(error));
  };
};

// WIZARD
const onExit = (payload) => ({
  type: types.SET_EXIT,
  payload,
});

export const setExit = (exit, type) => (dispatch) => {
  return dispatch(onExit({ exit, type }));
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

export const changePosition = (type, exit) => (dispatch, getState) => {
  if (exit) {
    return dispatch(setExit(exit, 'exitOnboarding'));
  }

  const {
    wizard: { position },
  } = getState().invoice;

  const nextPosition = type === 'next' ? 1 : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

// Reset onboarding
export const resetWizard = () => (dispatch) => {
  return dispatch({ type: types.RESET_WIZARD });
};

// CREATE INVOICE
const fetchCreateInvoiceRequest = () => ({
  type: types.FETCH_CREATE_INVOICE_REQUEST,
});

const fetchCreateInvoiceSuccess = (payload) => ({
  type: types.FETCH_CREATE_INVOICE_SUCCESS,
  payload,
});

const fetchCreateInvoiceFailed = (errors) => ({
  type: types.FETCH_CREATE_INVOICE_FAILED,
  errors,
});

export const fetchCreateInvoice = (payload, exit, onSuccess) => {
  return async (dispatch) => {
    dispatch(fetchCreateInvoiceRequest());
    const response = await invoiceActions.fetchCreateInvoice(payload);

    if (response.ok) {
      if (exit) {
        dispatch(setExit(exit, 'exitOnboarding'));
        return dispatch(
          uiActions.handleShowSnackbar({
            type: 'success',
            message: i18n.t('invoice.created_successfully_draft'),
          })
        );
      }
      dispatch(changePosition('next'));
      return dispatch(fetchCreateInvoiceSuccess(response.data));
    }

    const formattedError = getErrorMessage(response?.data, {}, true);

    if (typeof formattedError === 'string' || formattedError?.length === 1) {
      dispatch(fetchCreateInvoiceFailed());

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError?.[0]?.message || formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchCreateInvoiceFailed(errors));
  };
};

// DELETE INVOICE
export const fetchDeleteInvoice = (id) => {
  return async (dispatch) => {
    const response = await invoiceActions.fetchDeleteInvoice(id);

    if (response.ok) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.deleted_successfully'),
        })
      );
    }

    const formattedError = getErrorMessage(response?.data, {}, true);

    if (typeof formattedError === 'string' || formattedError?.length === 1) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError?.[0]?.message || formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });
  };
};

// UPDATE INVOICE
const fetchUpdateInvoiceRequest = () => ({
  type: types.FETCH_UPDATE_INVOICE_REQUEST,
});

const fetchUpdateInvoiceSuccess = (payload) => ({
  type: types.FETCH_UPDATE_INVOICE_SUCCESS,
  payload,
});

const fetchUpdateInvoiceFailed = (errors) => ({
  type: types.FETCH_UPDATE_INVOICE_FAILED,
  errors,
});

export const fetchUpdateInvoice = (id, payload, mode, onSuccess) => {
  return async (dispatch) => {
    dispatch(fetchUpdateInvoiceRequest());
    const response = await invoiceActions.fetchUpdateInvoice(id, payload);

    if (response.ok) {
      if (onSuccess) {
        onSuccess();
        dispatch(
          uiActions.handleShowSnackbar({
            type: 'success',
            message: i18n.t('invoice.updated_successfully'),
          })
        );
      }

      if (mode !== 'modal') {
        dispatch(changePosition('next'));
      }
      return dispatch(fetchUpdateInvoiceSuccess(response.data));
    }

    const formattedError = getErrorMessage(response?.data, {}, true);

    if (typeof formattedError === 'string' || formattedError?.length === 1) {
      dispatch(fetchUpdateInvoiceFailed());

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError?.[0]?.message || formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchUpdateInvoiceFailed(errors));
  };
};

// PUBLISH INVOICE
const fetchPublishInvoiceRequest = () => ({
  type: types.FETCH_PUBLISH_INVOICE_REQUEST,
});

const fetchPublishInvoiceSuccess = (payload) => ({
  type: types.FETCH_PUBLISH_INVOICE_SUCCESS,
  payload,
});

const fetchPublishInvoiceFailed = (errors) => ({
  type: types.FETCH_PUBLISH_INVOICE_FAILED,
  errors,
});

export const fetchPublishInvoice = (onExit) => {
  return async (dispatch, getState) => {
    dispatch(fetchPublishInvoiceRequest());
    const { invoice } = getState().invoice;
    const response = await invoiceActions.fetchUpdateInvoice(invoice.id, { is_draft: false });

    if (response.ok) {
      if (onExit) {
        dispatch(setExit(true, 'exitOnboarding'));
      }
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.published_successfully'),
        })
      );
      return dispatch(fetchPublishInvoiceSuccess(response.data));
    }

    const formattedError = getErrorMessage(response?.data, {}, true);

    if (typeof formattedError === 'string' || formattedError?.length === 1) {
      dispatch(fetchPublishInvoiceFailed());

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError?.[0]?.message || formattedError,
        })
      );
    }
    dispatch(fetchPublishInvoiceFailed());
  };
};

// UPDATE INVOICE APPROVAL INVOICE
const fetchUpdateInvoiceApprovalRequest = () => ({
  type: types.FETCH_UPDATE_INVOICE_APPROVAL_REQUEST,
});

const fetchUpdateInvoiceApprovalSuccess = (payload) => ({
  type: types.FETCH_UPDATE_INVOICE_APPROVAL_SUCCESS,
  payload,
});

const fetchUpdateInvoiceApprovalFailed = (errors) => ({
  type: types.FETCH_UPDATE_INVOICE_APPROVAL_FAILED,
  errors,
});

export const fetchUpdateInvoiceApproval = (id, status) => {
  return async (dispatch) => {
    dispatch(fetchUpdateInvoiceApprovalRequest());

    const response = await invoiceActions.fetchUpdateInvoiceApproval(id, { status });

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('invoice.updated_success'),
        })
      );
      return dispatch(fetchUpdateInvoiceApprovalSuccess(response.data));
    }

    const formattedError = getErrorMessage(response?.data, {}, true);

    if (typeof formattedError === 'string' || formattedError?.length === 1) {
      dispatch(fetchUpdateInvoiceApprovalFailed());

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError?.[0]?.message || formattedError,
        })
      );
    }
    dispatch(fetchUpdateInvoiceApprovalFailed());
  };
};
